import type { TFunction } from "i18next";
import { supabase } from "@/lib/supabase";
import { fetchAssignedLocationIds } from "@/lib/cleanerAssignedLocations";
import { isAssignmentPlaceholderSection } from "@/lib/locationAccess";
import { filterTasksForDisplay, type TaskFilterContext } from "@/lib/task-filters";
import { calculateTaskProgress } from "@/lib/task-progress";
import { Database } from "@/integrations/supabase/types";
import { parseChecklistItems, type ChecklistJsonItem } from "@/lib/checklistJson";

type CleaningClient = Database["public"]["Tables"]["cleaning_clients"]["Row"];

export interface CleanerPropertyWithDetails {
  id: string;
  address: string;
  clientName?: string;
  priority: "low" | "medium" | "high" | "urgent";
  status: "pending" | "in_progress" | "completed";
  tasksTotal: number;
  tasksCompleted: number;
  tasksRemaining: number;
  sections: CleanerSectionWithDetails[];
  inventory: CleanerInventoryItem[];
  coordinatorNotes?: string;
  qr_code_token?: string | null;
  checklist?: unknown;
  latitude: number | null;
  longitude: number | null;
  location_master_id?: string | null;
  validation_config?: {
    qr_required?: boolean;
    gps_required?: boolean;
    biometric_required?: boolean;
  } | null;
}

export interface CleanerSectionWithDetails {
  id: string;
  name: string;
  assignedTo?: string;
  isAssignedToMe: boolean;
  allowCrossAccess: boolean;
  tasksTotal: number;
  tasksCompleted: number;
  checklistTasks: CleanerChecklistTask[];
  inventory: CleanerInventoryItem[];
  activityLog: CleanerActivityLogEntry[];
  instructionNotes?: string;
  accessCode?: string;
  taskId?: string;
}

export interface CleanerChecklistTask {
  id: string;
  name: string;
  completed: boolean;
  note?: string;
  type: "recurring" | "adhoc";
  task_type?: "sop_standard" | "coordinator_single" | "long_term" | "employee_extra";
  status?: "active" | "moved";
  requiresPhoto?: boolean;
  photoTaken?: boolean;
  is_extra_task?: boolean;
  originTaskId?: string;
}

interface CleanerInventoryItem {
  id: string;
  name: string;
  unit: string;
  ordered?: boolean;
}

interface CleanerActivityLogEntry {
  id: string;
  person: string;
  action: string;
  timestamp: string;
}

export async function fetchCleanerDashboardProperties(
  orgId: string,
  t: TFunction,
  signal?: AbortSignal
): Promise<CleanerPropertyWithDetails[]> {
      const { data: { user } } = await supabase.auth.getUser();
      const currentUserId = user?.id;

      if (!currentUserId) {
        throw new Error(t('errors.noUser'));
      }

      const assignedLocationIds = await fetchAssignedLocationIds(currentUserId);
      if (assignedLocationIds.length === 0) {
        console.log("[fetchCleanerDashboardProperties] Brak przypisanych lokalizacji dla użytkownika:", currentUserId);
        return [];
      }

      if (signal?.aborted) {
        console.log("[fetchCleanerDashboardProperties] aborted");
        return [];
      }

      const { data: locations, error: locationsError } = await supabase
        .from('cleaning_locations')
        .select(`
          id,
          address,
          instruction_notes,
          access_code,
          latitude,
          longitude,
          geofence_radius_meters,
          qr_code_token,
          location_master_id,
          validation_config,
          cleaning_clients (
            id,
            name
          )
        `)
        .eq('org_id', orgId)
        .eq('is_cleaning_active', true)
        .eq('status', 'active')
        .in('id', assignedLocationIds)
        .order('id', { ascending: true });

      if (locationsError) {
        throw new Error(`${t('errors.unexpectedError')}: ${locationsError.message}`);
      }

      // Pobierz zadania: przypisane do pracownika LUB nieprzypisane (SOP, odśnieżanie) dla jego lokalizacji
      const locationIds = Array.isArray(locations) ? locations.map(l => l.id).filter(Boolean) : [];
      
      // KRYTYCZNE: Sprawdź, czy locations jest faktycznie pustą tablicą, czy null/undefined (błąd wybudzania sesji)
      if (locationIds.length === 0) {
        if (!Array.isArray(locations)) {
          throw new Error("Sesja niegotowa — brak listy lokalizacji");
        }
        console.log("[fetchCleanerDashboardProperties] Brak lokalizacji dla org_id:", orgId);
        return [];
      }

      if (signal?.aborted) {
        console.log("[fetchCleanerDashboardProperties] aborted");
        return [];
      }

      console.log('[CleanerDashboard] Pobieranie zadań dla org_id:', orgId, 'locationIds:', locationIds.length);
      
      // Kontekst dat dla scentralizowanego filtrowania (task-filters.ts)
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      const filterContext: TaskFilterContext = { todayStart, todayEnd };

      // Zakres pobierania: 30 dni wstecz do końca dzisiaj (zaległości + dziś)
      const thirtyDaysAgo = new Date(todayStart);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAhead = new Date(todayEnd);
      thirtyDaysAhead.setDate(thirtyDaysAhead.getDate() + 31);
      const thirtyDaysAgoISO = thirtyDaysAgo.toISOString();
      const thirtyDaysAheadISO = thirtyDaysAhead.toISOString();

      const { data: tasks, error: tasksError } = await supabase
        .from('cleaning_tasks')
        .select(`
          id,
          location_id,
          section_id,
          status,
          priority,
          assigned_staff_id,
          created_at,
          completed_at,
          coordinator_notes,
          checklist,
          task_type,
          scheduled_at,
          check_in_metadata
        `)
        .eq('org_id', orgId)
        .in('location_id', locationIds)
        .neq('status', 'cancelled')
        .or(
          `and(scheduled_at.gte.${thirtyDaysAgoISO},scheduled_at.lte.${thirtyDaysAheadISO}),task_type.eq.coordinator_single,task_type.eq.employee_extra,scheduled_at.is.null`
        )
        .order('id', { ascending: true });
      
      console.log('[CleanerDashboard] Pobrano zadań:', tasks?.length || 0, 'dla org_id:', orgId);

      if (tasksError) {
        throw new Error(`${t('errors.unexpectedError')}: ${tasksError.message}`);
      }

      // Pobierz sekcje dla wszystkich lokalizacji (tylko aktywne)
      const { data: sections, error: sectionsError } = await supabase
        .from('property_sections')
        .select(`
          id,
          name,
          location_id,
          assigned_staff_id,
          allow_cross_access
        `)
        .in('location_id', locationIds)
        .eq('is_active', true);

      if (sectionsError) {
        throw new Error(`${t('errors.unexpectedError')}: ${sectionsError.message}`);
      }

      const visibleSections = (sections ?? []).filter(
        (section) => !isAssignmentPlaceholderSection(section.name)
      );

      // Pobierz produkty z cleaning_inventory dla wszystkich lokalizacji z relacją do katalogu
      const { data: inventoryItems, error: inventoryError } = await supabase
        .from('cleaning_inventory')
        .select('id, item_name, unit, location_id, cleaning_catalog(name, unit)')
        .in('location_id', locationIds);

      if (inventoryError) {
        console.error('[CleanerDashboard] Błąd pobierania inventory:', inventoryError);
        // Nie przerywamy - kontynuujemy bez inventory
      }

      // UWAGA: Nie używamy już property_checklists - używamy checklist JSONB z cleaning_tasks
      // Pole 'checklist' jest już pobierane w zapytaniu cleaning_tasks (linia 145)

      // Pobierz logi aktywności z task_step_logs (nowa tabela)
      const taskIds = Array.isArray(tasks) ? tasks.map(t => t.id).filter(Boolean) : [];
      let activityLogs: Array<{
        id: string;
        task_id: string;
        user_id: string;
        step_name: string;
        created_at: string;
      }> = [];
      
      if (taskIds.length > 0) {
        const { data: logs, error: logsError } = await supabase
          .from('task_step_logs')
          .select(`
            id,
            task_id,
            user_id,
            step_name,
            created_at
          `)
          .in('task_id', taskIds)
          .order('created_at', { ascending: false })
          .limit(100);

        if (logsError) {
          console.error('Błąd pobierania logów aktywności:', logsError);
          // Nie przerywamy - kontynuujemy bez logów aktywności
        } else {
          // Obsługa pustych danych po RLS: task_step_logs może zwrócić pustą tablicę jeśli użytkownik nie ma dostępu
          activityLogs = Array.isArray(logs) ? logs : [];
        }
      }

      // Pobierz informacje o użytkownikach dla logów aktywności
      const userIds = [...new Set(activityLogs.map(log => log.user_id).filter(Boolean))];
      let userNames: Record<string, string> = {};
      
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds as string[]);

        if (!profilesError && Array.isArray(profiles)) {
          userNames = Object.fromEntries(
            profiles.map(p => [p.id, p.full_name || t('common.unknown')])
          );
        }
      }

      // Buduj mapę sekcji per lokalizacja (dla buildTaskCounters)
      const sectionsByLocation = new Map<string, Array<{ id: string }>>();
      for (const s of visibleSections) {
        const locId = s.location_id;
        if (!sectionsByLocation.has(locId)) sectionsByLocation.set(locId, []);
        sectionsByLocation.get(locId)!.push({ id: s.id });
      }
      for (const loc of Array.isArray(locations) ? locations : []) {
        if (!sectionsByLocation.has(loc.id)) {
          sectionsByLocation.set(loc.id, [{ id: `default-${loc.id}` }]);
        }
      }

      // Scentralizowana logika liczników (zgodna z Widokiem Bieżącym – task-progress.ts)
      const taskCounters = calculateTaskProgress(
        Array.isArray(tasks) ? tasks : [],
        sectionsByLocation,
        filterContext
      );

      // Przygotuj dane o właściwościach
      // Stale-While-Revalidate: Użyj tempData - tylko po SUKCESIE zapytania wykonaj setProperties
      const tempPropertiesWithDetails: CleanerPropertyWithDetails[] = (Array.isArray(locations) ? locations : []).map(location => {
        const locationTasksRaw = Array.isArray(tasks) ? tasks.filter(t => t.location_id === location.id) : [];
        const filtered = filterTasksForDisplay(locationTasksRaw, filterContext);
        // Display order: overdue first (urgent), then today pending, then today done (strikethrough for correction)
        // Kolejność wyświetlania: zaległe (overdue), dziś pending, dziś done – wszystkie wliczane do liczników
        const displayTasksForLocation = [
          ...filtered.overdue,
          ...filtered.todayPending,
          ...filtered.todayDone,
        ];
        const locationTasks = displayTasksForLocation;
        const locationSections = visibleSections.filter(s => s.location_id === location.id);

        // Liczniki z nowej logiki (buildTaskCounters)
        const counters = taskCounters[location.id] ?? { total: 0, completed: 0, sections: {} };

        // Agregacja Checklisty: scalać rekordy do wyświetlenia (dziś: pending/done + zaległe)
        let locationChecklist: unknown = null;
        const uniqueChecklistMap = new Map<string, ChecklistJsonItem>();

        if (locationTasks.length > 0) {
          const tasksToProcess = locationTasks;
          
          // KROK 3: Przejdź przez wszystkie zadania do przetworzenia i złącz ich checklisty
          // Sortuj po created_at (najnowsze pierwsze) - nowsze zadania mają priorytet przy scalaniu
          tasksToProcess.sort((a, b) => {
            const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
            if (timeA !== timeB) return timeB - timeA; // Najnowsze pierwsze
            return b.id.localeCompare(a.id);
          });
          
          // Zachowaj pierwszy niepusty checklist dla kompatybilności wstecznej (używany w sekcjach)
          // Pomijaj zadania z pustą checklistą (np. sesje odśnieżania – employee_extra bez checklisty)
          for (const task of tasksToProcess) {
            const checklist = task.checklist;
            
            if (!checklist) continue;
            const isEmptyChecklist = Array.isArray(checklist)
              ? checklist.length === 0
              : typeof checklist === 'object' && Object.keys(checklist).length === 0;
            if (isEmptyChecklist) continue;
            
            // Zachowaj pierwszy niepusty checklist
            if (!locationChecklist) {
              locationChecklist = checklist;
            }
            
            const checklistArray = parseChecklistItems(checklist);
            
            // Scalać zadania do unikalnej Mapy (klucz: nazwa zadania)
            // Priorytet: jeśli zadanie jest completed: true w którymkolwiek rekordzie, zachowaj completed: true
            // FIX: Ujednolicenie nazewnictwa - zawsze używaj item.name (zgodnie z modelem 4-typów)
            // FIX: Ignoruj zadania z statusem 'moved' - nie dodawaj ich do mapy (nie są zadaniem na dziś)
            for (const item of checklistArray) {
              const taskName = item.name || item.task_name || item.id || '';
              
              if (!taskName) continue; // Pomijaj zadania bez nazwy
              
              // FIX: Pomijaj zadania z statusem 'moved' - nie są zadaniem na dzisiaj
              if (item.status === 'moved') {
                continue;
              }
              
              if (!uniqueChecklistMap.has(taskName)) {
                // Nowe zadanie - dodaj do Mapy
                uniqueChecklistMap.set(taskName, item);
              } else {
                // Zadanie już istnieje - zaktualizuj jeśli nowe ma completed: true (priorytet wykonania)
                const existingTask = uniqueChecklistMap.get(taskName);
                if (existingTask && item.completed === true && !existingTask.completed) {
                  uniqueChecklistMap.set(taskName, { ...existingTask, completed: true });
                }
              }
            }
          }
          
          console.log(`[CleanerDashboard] Scalone zadania dla lokalizacji ${location.id}: tasksToProcess=${tasksToProcess.length}, uniqueTasks=${uniqueChecklistMap.size}`);
        }

        // LOGIKA SEKCJI (FALLBACK): Jeśli brak sekcji, stwórz domyślną sekcję, aby uniknąć pustej listy
        const effectiveSections = locationSections.length > 0 
          ? locationSections 
          : [{ 
              id: `default-${location.id}`, 
              name: 'Główna', 
              location_id: location.id,
              assigned_staff_id: null,
              allow_cross_access: true,
              created_at: new Date().toISOString()
            }];

        // Przygotuj sekcje z szczegółami - używamy tej samej scalonej Mapy (uniqueChecklistMap)
        const firstSectionId = effectiveSections[0]?.id ?? null;
        const sectionsWithDetails: CleanerSectionWithDetails[] = effectiveSections.map(section => {
          // Uwzględnij: section_id, coordinator_single (cały budynek), employee_extra (sekcja lub pierwsza przy null)
          const sectionTasks = locationTasks.filter(t => {
            const sectionMatches = t.section_id === section.id;
            const isCoordinatorTask = t.task_type === 'coordinator_single';
            const isOrphanInFirstSection = !t.section_id && (t.task_type === 'employee_extra' || t.task_type === 'coordinator_single') && section.id === firstSectionId;
            return sectionMatches || isCoordinatorTask || isOrphanInFirstSection;
          });
          
          // Zbierz ID zadań sekcji dla logów aktywności (wszystkie zadania przypisane do tej sekcji)
          const sectionTaskIds = new Set(sectionTasks.map(t => t.id));
          
          // FIX: Zamiast używać uniqueChecklistMap (która scala zadania po nazwie), 
          // rozwijamy wszystkie zadania z checklisty JSONB bezpośrednio z rekordów cleaning_tasks
          // To zapewnia, że każde zadanie z bazy jest liczone osobno, nawet jeśli ma tę samą nazwę
          const normalizedSectionId = section.id?.toString().toLowerCase();
          const checklistTasks: CleanerChecklistTask[] = [];
          
          // Przejdź przez wszystkie zadania z sekcji i rozwiń checklistę JSONB na pojedyncze zadania
          for (const taskRecord of sectionTasks) {
            const checklist = taskRecord.checklist;
            
            // Pomijaj zadania bez checklisty (będą obsłużone jako adhoc tasks)
            if (!checklist) continue;
            
            const checklistArray = parseChecklistItems(checklist);
            
            // Rozwiń każdy element checklisty jako osobne zadanie
            for (const item of checklistArray) {
              // Pomijaj zadania z statusem 'moved' - nie są zadaniem na dzisiaj
              if (item.status === 'moved') {
                continue;
              }
              
              // Sprawdź czy zadanie należy do tej sekcji
              const taskSectionId = item?.section_id ? item.section_id.toString().toLowerCase() : null;
              const isCoordinatorTask = taskRecord.task_type === 'coordinator_single';
              const sectionMatches = taskSectionId && normalizedSectionId && taskSectionId === normalizedSectionId;
              
              // Wyświetl jeśli section_id się zgadza LUB jest to coordinator_single
              if (sectionMatches || isCoordinatorTask) {
                checklistTasks.push({
                  id: item.id || `${taskRecord.id}-${item.name || item.task_name || item.id || 'task'}`,
                  name: item.name || item.task_name || 'Zadanie bez nazwy',
                  completed: item.completed || false,
                  type: (item.type || 'recurring') as 'recurring' | 'adhoc',
                  status: item.status === "moved" ? "moved" : "active",
                  requiresPhoto: item.photo_required || item.requires_photo || false,
                  photoTaken: (item.photo_urls || []).length > 0 || false,
                  originTaskId: taskRecord.id, // Zapisz ID rekordu cleaning_tasks dla aktualizacji
                });
              }
            }
          }

          // Adhoc tasks - zadania z cleaning_tasks bez checklisty LUB coordinator_single bez checklisty
          // Wszystkie zadania z bazy już spełniają warunki (przefiltrowane przez SQL):
          // - sop_standard: tylko z dzisiejszą datą (scheduled_at = dzisiaj)
          // - long_term: widoczne jeśli scheduled_at <= CURRENT_DATE AND status != 'done' (zaległe i dzisiejsze)
          // - coordinator_single: wszystkie nieukończone (status != 'done')
          // - employee_extra: wszystkie nieukończone (status != 'done')
          const checklistTaskOriginIds = new Set(checklistTasks.map(t => t.originTaskId).filter(Boolean));
          
          const adhocTasks: CleanerChecklistTask[] = sectionTasks
            .filter(t => {
              // Pomijaj zadania, które już mają checklistę rozwiniętą w checklistTasks
              if (checklistTaskOriginIds.has(t.id)) {
                // Sprawdź czy zadanie ma checklistę - jeśli nie, dodaj jako adhoc
                const hasChecklist = t.checklist && (
                  (Array.isArray(t.checklist) && t.checklist.length > 0) ||
                  (typeof t.checklist === 'object' && Object.keys(t.checklist).length > 0)
                );
                // Jeśli ma checklistę, pomiń (już jest w checklistTasks)
                if (hasChecklist) return false;
              }
              
              // Wyświetl: section_id, coordinator_single (cały budynek), employee_extra bez sekcji w pierwszej sekcji
              const sectionMatches = t.section_id === section.id;
              const isCoordinatorTask = t.task_type === 'coordinator_single';
              const isOrphanInFirstSection = !t.section_id && (t.task_type === 'employee_extra' || t.task_type === 'coordinator_single') && section.id === firstSectionId;
              return sectionMatches || isCoordinatorTask || isOrphanInFirstSection;
            })
            .map(t => {
              // LOGIKA WYŚWIETLANIA NAZW: coordinator_notes > checklist[0].name > fallback
              let taskName = '';
              if (t.coordinator_notes) {
                taskName = t.coordinator_notes;
              } else {
                const firstItem = parseChecklistItems(t.checklist)[0];
                taskName = firstItem?.name || firstItem?.task_name || (t.task_type === 'employee_extra' ? 'Zadanie dodatkowe' : 'Zadanie bez nazwy');
              }
              
              return {
                id: t.id,
                name: taskName,
                completed: t.status === 'done',
                type: 'adhoc' as const,
                task_type: t.task_type as 'sop_standard' | 'coordinator_single' | 'long_term' | 'employee_extra' | undefined,
                status: 'active' as const, // FIX: Adhoc tasks są zawsze aktywne (nie można ich przenosić)
                requiresPhoto: false,
                is_extra_task: t.task_type === 'employee_extra',
                originTaskId: t.id, // Zapisz ID rekordu cleaning_tasks dla aktualizacji
              };
            });

          // FIX: Filtruj zadania z statusem 'moved' z allTasks - nie powinny być liczone ani wyświetlane
          const allTasks = [...checklistTasks, ...adhocTasks].filter(t => t.status !== 'moved');
          
          // Liczniki z nowej logiki (buildTaskCounters)
          const sectionCounters = counters.sections[section.id] ?? { total: 0, completed: 0 };
          const tasksTotal = sectionCounters.total;
          const tasksCompleted = sectionCounters.completed;
          const tasksRemainingSection = tasksTotal - tasksCompleted;

          // Przygotuj logi aktywności dla sekcji
          const sectionTaskLogs = activityLogs.filter(log => 
            log.task_id && sectionTaskIds.has(log.task_id)
          );
          
          const sectionActivityLog: CleanerActivityLogEntry[] = sectionTaskLogs
            .slice(0, 10)
            .map(log => ({
              id: log.id,
              person: userNames[log.user_id || ''] || t('common.unknown'),
              action: log.step_name || t('common.unknown'), // Użyj step_name zamiast action_type
              timestamp: log.created_at 
                ? new Date(log.created_at).toLocaleTimeString('pl-PL', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })
                : '',
            }));

          // Znajdź główne zadanie dla sekcji (pierwsze zadanie z section_id)
          const mainTask = sectionTasks.length > 0 ? sectionTasks[0] : null;
          const mainTaskId = mainTask?.id || undefined;

          return {
            id: section.id,
            name: section.name,
            assignedTo: section.assigned_staff_id ? undefined : undefined,
            isAssignedToMe:
              !section.assigned_staff_id || section.assigned_staff_id === currentUserId,
            allowCrossAccess: section.allow_cross_access ?? true,
            tasksTotal: tasksTotal, // Suma wszystkich zadań z checklisty JSONB
            tasksCompleted: tasksCompleted, // Suma ukończonych zadań z checklisty JSONB
            checklistTasks: allTasks,
            inventory: [],
            activityLog: sectionActivityLog,
            instructionNotes: location.instruction_notes || undefined,
            accessCode: location.access_code || undefined,
            taskId: mainTaskId,
          };
        });

        const client = Array.isArray(location.cleaning_clients) 
          ? location.cleaning_clients[0] 
          : location.cleaning_clients as CleaningClient | null;

        // Znajdź coordinator_notes z aktywnych zadań dla tej lokalizacji
        // Szukamy we wszystkich aktywnych zadaniach, nie tylko pierwszym
        const taskWithNotes = locationTasks.find(t => t.coordinator_notes);
        const coordinatorNotes = taskWithNotes?.coordinator_notes || undefined;

        // Pobierz produkty z cleaning_inventory dla tej lokalizacji; nazwa/jednostka z katalogu lub fallback
        type InvRow = { id: string; item_name?: string; unit?: string | null; location_id?: string | null; cleaning_catalog?: { name?: string; unit?: string } | null };
        const locationInventory = Array.isArray(inventoryItems)
          ? (inventoryItems as InvRow[])
              .filter(item => item.location_id === location.id)
              .map(item => ({
                id: item.id,
                name: (item.cleaning_catalog?.name ?? item.item_name) || '',
                unit: item.cleaning_catalog?.unit ?? item.unit ?? 'szt',
                ordered: false,
              }))
          : [];

        // Agreguj inventory z wszystkich sekcji dla tego property (fallback - jeśli sekcje mają swoje produkty)
        const inventoryMap = new Map<string, CleanerInventoryItem>();
        
        sectionsWithDetails.forEach(section => {
          section.inventory.forEach(item => {
            // Unikaj duplikatów - używamy Map z id jako kluczem
            if (!inventoryMap.has(item.id)) {
              inventoryMap.set(item.id, item);
            }
          });
        });
        
        // Jeśli mamy produkty z bazy (location_id), użyj ich zamiast sekcji
        const aggregatedInventory = locationInventory.length > 0 
          ? locationInventory 
          : Array.from(inventoryMap.values());

        // Liczniki karty budynku z nowej logiki (buildTaskCounters)
        const tasksTotal = counters.total;
        const tasksCompleted = counters.completed;
        const tasksRemaining = tasksTotal - tasksCompleted;

        // Oblicz status właściwości na podstawie sumy zadań ze wszystkich sekcji
        // Budynek powinien mieć status completed TYLKO wtedy, gdy suma zadań we wszystkich jego sekcjach jest większa od zera i wszystkie są wykonane
        const inProgressTasks = locationTasks.filter(t => t.status === 'in_progress').length;
        
        let status: 'pending' | 'in_progress' | 'completed';
        if (tasksTotal > 0 && tasksCompleted === tasksTotal) {
          status = 'completed';
        } else if (inProgressTasks > 0 || tasksCompleted > 0) {
          status = 'in_progress';
        } else {
          status = 'pending';
        }

        // Oblicz priorytet (najwyższy priorytet zadań)
        const priorities = locationTasks
          .map(t => t.priority)
          .filter((p): p is 'low' | 'medium' | 'high' | 'emergency' => p !== null);
        
        let priority: 'low' | 'medium' | 'high' | 'urgent' = 'low';
        if (priorities.includes('emergency')) priority = 'urgent';
        else if (priorities.includes('high')) priority = 'high';
        else if (priorities.includes('medium')) priority = 'medium';

        return {
          id: location.id,
          address: location.address,
          clientName: client?.name,
          priority,
          status,
          tasksTotal: tasksTotal,
          tasksCompleted: tasksCompleted,
          tasksRemaining: tasksRemaining,
          sections: sectionsWithDetails,
          inventory: aggregatedInventory,
          coordinatorNotes,
          qr_code_token: location.qr_code_token || null,
          location_master_id: (location as { location_master_id?: string | null }).location_master_id || null,
          checklist: locationChecklist, // Dodaj checklist JSONB do property
          latitude: (location as { latitude?: number | null }).latitude ?? null,
          longitude: (location as { longitude?: number | null }).longitude ?? null,
          validation_config: location.validation_config as {
            qr_required?: boolean;
            gps_required?: boolean;
            biometric_required?: boolean;
          } | null,
        };
      });

      return tempPropertiesWithDetails;
}
